// The star a screen puts on what it shows, to add it to or remove it from the
// favorites of a place ("me" for personal things).
import { m } from "@trameria/i18n";
import { apiErrorWithLead } from "../data/errors";
import type { ShellActionInput } from "../shell/outside/outside";
import { ErrorLine } from "../ui/bits";
import { IconButton } from "../ui/button";
import { findFavorite, useFavorites, useToggleFavorite } from "./favorites";

export function FavoriteStar({
  kind,
  targetId,
  title,
  place = "me",
}: {
  kind: string;
  targetId: string;
  title: string;
  place?: string;
}) {
  const list = useFavorites(place);
  const toggle = useToggleFavorite(place);
  const current = findFavorite(list.data, kind, targetId);
  const on = current !== undefined;
  // Only for this target: the same star may be reused for the next thing shown.
  const failed =
    toggle.isError &&
    toggle.variables?.kind === kind &&
    toggle.variables.targetId === targetId
      ? toggle.variables
      : null;
  return (
    <div className="relative flex flex-none">
      <IconButton
        label={on ? m.shell_favorites_remove() : m.shell_favorites_add()}
        aria-pressed={on}
        disabled={!list.isSuccess}
        className={on ? "text-warn" : ""}
        onClick={() =>
          toggle.mutate({ kind, targetId, title, removeId: current?.id })
        }
      >
        <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M8 1.9l1.85 3.9 4.25.55-3.1 2.95.8 4.2L8 11.45 4.2 13.5l.8-4.2-3.1-2.95 4.25-.55z"
            fill={on ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
        </svg>
      </IconButton>
      {failed && (
        // Right under the star, floating so the header row keeps its height.
        <div className="absolute top-full right-0 z-10 mt-1 w-max max-w-[320px] rounded-[7px] border border-border bg-surface px-2.5 pb-1 shadow-[var(--shadow-popover)]">
          <ErrorLine>
            {apiErrorWithLead(
              toggle.error,
              failed.removeId
                ? m.shell_favorites_remove_failed({ title: failed.title })
                : m.shell_favorites_add_failed({ title: failed.title }),
            )}
          </ErrorLine>
        </div>
      )}
    </div>
  );
}

/** The star as a header button's data (shell/outside), toggling as the star does. */
export function useFavoriteAction({
  kind,
  targetId,
  title,
  place = "me",
}: {
  kind: string;
  targetId: string;
  title: string;
  place?: string;
}): ShellActionInput {
  const list = useFavorites(place);
  const toggle = useToggleFavorite(place);
  const current = findFavorite(list.data, kind, targetId);
  const on = current !== undefined;
  return {
    id: "favorite",
    label: on ? m.shell_favorites_remove() : m.shell_favorites_add(),
    icon: "star",
    on,
    ...(list.isSuccess ? {} : { disabled: true }),
    onPress: () =>
      toggle.mutate({ kind, targetId, title, removeId: current?.id }),
  };
}
